// chat/dayLabel — the pure half of the WhatsApp day divider.
//
// MIRRORED FILE: byte-identical in DispatchSystem/services/chat/ and
// wolfdriver-mobile/services/chat/. Imports only siblings. See MIRROR.md.
//
// Wording (Umer, 2026-09-14, FINAL): EXACTLY what the direct/order chat has
// shipped with — 'Today', 'Yesterday', the weekday name for the rest of the
// last seven days, then a short date ('Apr 21, 2026'). This file is that
// existing `dayDividerLabel` logic (web components/OrderChat.tsx, mobile
// components/ChatModal.tsx) lifted out so group chat and the SMS Text Inbox
// use the same one instead of each repo carrying a copy.
//
// Why the split: deciding WHICH shape a day gets is calendar logic and must be
// identical on web and mobile; producing the words ('Thursday', 'Apr 21, 2026')
// needs Intl with the Ottawa timezone, which lives in each repo's
// services/appTime.ts (web caches formatters, mobile does not). So this file
// takes day KEYS ('YYYY-MM-DD', already in Ottawa time) plus pre-formatted
// pieces, and owns nothing platform-specific.

/**
 * Pure calendar stepping over a 'YYYY-MM-DD' key. UTC is used only as an inert
 * calendar — the key is already in Ottawa time, so this never re-enters a
 * timezone and is therefore DST-proof: 2026-03-08 (spring forward, a 23-hour
 * day) and 2026-11-01 (fall back, 25 hours) step by exactly one calendar day
 * like any other. Stepping epoch ms by 86_400_000 does not.
 */
export function addDaysToDayKey(dayKey: string, days: number): string {
  const [y, m, d] = dayKey.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  const p2 = (n: number) => (n < 10 ? `0${n}` : String(n));
  return `${dt.getUTCFullYear()}-${p2(dt.getUTCMonth() + 1)}-${p2(dt.getUTCDate())}`;
}

export type DayLabelKind = 'today' | 'yesterday' | 'weekday' | 'date';

/** How far back a day is from today, in calendar days; -1 when it is 7+ back
 *  or in the FUTURE. The future case matters: a sender's skewed clock (or an
 *  imported row) must not be labelled 'Today', and the original loop this was
 *  lifted from only ever walked backwards, so the behaviour is preserved. */
export function daysBack(dayKey: string, todayKey: string, maxBack = 7): number {
  let cursor = todayKey;
  for (let i = 0; i < maxBack; i++) {
    if (dayKey === cursor) return i;
    cursor = addDaysToDayKey(cursor, -1);
  }
  return -1;
}

/**
 * Which shape of label a day gets, relative to today. Both arguments are
 * Ottawa-time day keys ('YYYY-MM-DD'), so a message sent at 23:30 Ottawa on
 * Dec 31 is 'Yesterday' at 00:10 on Jan 1 — the year rollover is just calendar
 * stepping, not arithmetic on epoch ms.
 */
export function dayLabelKind(dayKey: string, todayKey: string): DayLabelKind {
  const back = daysBack(dayKey, todayKey);
  if (back === 0) return 'today';
  if (back === 1) return 'yesterday';
  return back > 1 ? 'weekday' : 'date';
}

/** The words the platform's Intl produced for this day, in Ottawa time. */
export interface DayLabelParts {
  /** 'Thursday' — `{ weekday: 'long' }`. */
  weekday: string;
  /** 'Apr 21, 2026' — `{ year: 'numeric', month: 'short', day: 'numeric' }`. */
  date: string;
}

/** Assemble the divider text. */
export function formatDayLabel(kind: DayLabelKind, parts: DayLabelParts): string {
  if (kind === 'today') return 'Today';
  if (kind === 'yesterday') return 'Yesterday';
  return kind === 'weekday' ? parts.weekday : parts.date;
}
